import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { Capacitor } from "@capacitor/core";
/** Legacy build — better CID / font-face behavior on Android WebView. */
import * as pdfjs from "pdfjs-dist/legacy/build/pdf.mjs";
import { cloneArrayBuffer } from "../lib/file";
import {
  configurePdfJsWorker,
  defaultPdfTextLayerEnabled,
  pdfCMapUrl,
  pdfStandardFontDataUrl,
  pdfWorkerSrc,
  preloadPdfRuntime,
} from "../lib/pdfAssets";
import { APP_VERSION_CODE, APP_VERSION_NAME } from "../lib/constants";
import { appendLog } from "../lib/logger";

let workerConfigured = false;
const pdfErrors: string[] = [];
let assetsProbed = false;

/**
 * Options passed to pdfjs.getDocument.
 * On Android WebView, @font-face + fillText often re-shapes Persian/Arabic
 * glyphs incorrectly; path rendering draws CID outlines as the PDF intended.
 */
function pdfLoadOptions() {
  const native = Capacitor.isNativePlatform();
  return {
    isEvalSupported: false,
    cMapPacked: true,
    useSystemFonts: false,
    disableFontFace: native,
    verbosity: 0 as number,
    enableXfa: false,
  };
}

function ensurePdfWorker(): void {
  if (workerConfigured) return;
  configurePdfJsWorker(pdfjs);
  workerConfigured = true;
}

/** Fail loudly in logs if Capacitor asset paths for fonts/CMaps are broken. */
async function probePdfAssets(): Promise<void> {
  if (assetsProbed) return;
  assetsProbed = true;
  const probes = [
    `${pdfCMapUrl()}Adobe-CNS1-UCS2.bcmap`,
    `${pdfStandardFontDataUrl()}LiberationSans-Regular.ttf`,
    pdfWorkerSrc(),
  ];
  for (const url of probes) {
    try {
      const res = await fetch(url, { method: "GET", cache: "force-cache" });
      if (!res.ok) {
        const msg = `asset probe HTTP ${res.status}: ${url}`;
        pdfErrors.push(msg);
        appendLog("error", "pdf", msg);
      }
    } catch (err) {
      const msg = `asset probe failed: ${url} — ${
        err instanceof Error ? err.message : err
      }`;
      pdfErrors.push(msg);
      appendLog("error", "pdf", msg);
    }
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = window.setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`));
    }, ms);
    promise.then(
      (v) => {
        window.clearTimeout(t);
        resolve(v);
      },
      (err) => {
        window.clearTimeout(t);
        reject(err);
      }
    );
  });
}

export type PdfDiagnostics = {
  pdfjsVersion: string;
  workerUrl: string;
  cMapUrl: string;
  standardFontDataUrl: string;
  userAgent: string;
  pageCount: number;
  textLayerEnabled: boolean;
  samplePage1Text: string;
  errors: string[];
  generatedAt: string;
  app: {
    versionCode: number;
    versionName: string;
  };
  platform: {
    isNative: boolean;
    platform: string;
    capacitor: string;
    userAgent: string;
    language: string;
    onLine: boolean;
  };
  engine: {
    build: "legacy";
    renderMode: "canvas-only" | "canvas+textLayer";
    layoutMode: "continuous-vertical";
    dir: "ltr" | "rtl";
    lang: "fa";
    textLayerEnabled: boolean;
    disableFontFace: boolean;
    useSystemFonts: boolean;
    useWorkerFetch: boolean;
    cMapPacked: boolean;
    isEvalSupported: boolean;
    enableXfa: boolean;
    verbosity: number;
  };
  viewport: {
    containerWidth: number;
    fitScale: number;
    userZoom: number;
    renderScale: number;
    devicePixelRatio: number;
    page1AtScale1: { width: number; height: number } | null;
    windowInnerWidth: number;
    windowInnerHeight: number;
    visualViewportWidth: number | null;
    visualViewportHeight: number | null;
  };
};

export type PdfViewerHandle = {
  getDiagnostics: () => Promise<PdfDiagnostics>;
  setTextLayerEnabled: (on: boolean) => void;
};

export type PdfCommentPin = {
  id: string;
  page: number;
  xNorm: number;
  yNorm: number;
};

type Props = {
  buffer: ArrayBuffer;
  /** User zoom multiplier on top of fit-width (1 = phone width). */
  zoom: number;
  textLayerEnabled?: boolean;
  /** Visible comment pins on pages. */
  commentPins?: PdfCommentPin[];
  onPageCount: (n: number) => void;
  onVisiblePage?: (page: number) => void;
  onError?: (message: string) => void;
  /** Tap a comment pin → reopen that comment. */
  onCommentPinClick?: (id: string) => void;
  /** Long-press (~1s) on a page spot → comment at that location. */
  onLongPressComment?: (info: {
    page: number;
    quote: string;
    xNorm: number;
    yNorm: number;
  }) => void;
};

/** Shorter while pinching so canvas tracks fingers; slightly longer otherwise. */
const RENDER_DEBOUNCE_MS = 80;
const RENDER_DEBOUNCE_PINCH_MS = 16;

/**
 * Continuous vertical PDF. Zoom changes layout size via canvas render scale
 * (no CSS transform scale) so pan/scroll stay controllable when zoomed.
 * Adobe CMaps + standard fonts for Persian/Arabic CID (Identity-H).
 */
export const PdfViewer = forwardRef<PdfViewerHandle, Props>(function PdfViewer(
  {
    buffer,
    zoom,
    textLayerEnabled: textLayerProp = defaultPdfTextLayerEnabled(),
    commentPins = [],
    onPageCount,
    onVisiblePage,
    onError,
    onCommentPinClick,
    onLongPressComment,
  },
  ref
) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const pagesRef = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<pdfjs.PDFDocumentProxy | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [readyGen, setReadyGen] = useState(0);
  /** null until first fit-width measure — avoid oversized → zoom-out flash. */
  const [fitScale, setFitScale] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [renderScale, setRenderScale] = useState<number | null>(null);
  const [textLayerEnabled, setTextLayerEnabled] = useState(textLayerProp);
  const layoutReadyRef = useRef(false);
  const onPageCountRef = useRef(onPageCount);
  const onErrorRef = useRef(onError);
  const onVisiblePageRef = useRef(onVisiblePage);
  onPageCountRef.current = onPageCount;
  onErrorRef.current = onError;
  onVisiblePageRef.current = onVisiblePage;

  useEffect(() => {
    setTextLayerEnabled(textLayerProp);
  }, [textLayerProp]);

  const targetScale =
    fitScale == null
      ? null
      : Math.max(0.25, Math.min(4, fitScale * zoom));

  useImperativeHandle(ref, () => ({
    setTextLayerEnabled,
    getDiagnostics: async () => {
      let sample = "(no document loaded)";
      let page1AtScale1: { width: number; height: number } | null = null;
      try {
        if (pdf) {
          const pg = await withTimeout(pdf.getPage(1), 4000, "getPage(1)");
          try {
            const vp = pg.getViewport({ scale: 1 });
            page1AtScale1 = { width: vp.width, height: vp.height };
          } catch {
            /* ignore */
          }
          try {
            const tc = await withTimeout(
              pg.getTextContent(),
              4000,
              "getTextContent"
            );
            sample = tc.items
              .map((it) => ("str" in it ? String(it.str) : ""))
              .join("")
              .slice(0, 800);
          } catch (err) {
            sample = `extract failed: ${
              err instanceof Error ? err.message : err
            }`;
          }
        }
      } catch (err) {
        sample = `extract failed: ${err instanceof Error ? err.message : err}`;
      }

      const useWorkerFetch = !Capacitor.isNativePlatform();
      const vv = window.visualViewport;

      return {
        pdfjsVersion: (pdfjs as { version?: string }).version ?? "unknown",
        workerUrl: pdfWorkerSrc(),
        cMapUrl: pdfCMapUrl(),
        standardFontDataUrl: pdfStandardFontDataUrl(),
        userAgent: navigator.userAgent,
        pageCount,
        textLayerEnabled,
        samplePage1Text: sample,
        errors: pdfErrors.slice(-40),
        generatedAt: new Date().toISOString(),
        app: {
          versionCode: APP_VERSION_CODE,
          versionName: APP_VERSION_NAME,
        },
        platform: {
          isNative: Capacitor.isNativePlatform(),
          platform: Capacitor.getPlatform(),
          capacitor: Capacitor.isNativePlatform() ? "capacitor" : "web",
          userAgent: navigator.userAgent,
          language: navigator.language,
          onLine: navigator.onLine,
        },
        engine: {
          build: "legacy",
          renderMode: textLayerEnabled
            ? "canvas+textLayer"
            : "canvas-only",
          layoutMode: "continuous-vertical",
          dir: "ltr",
          lang: "fa",
          textLayerEnabled,
          disableFontFace: pdfLoadOptions().disableFontFace,
          useSystemFonts: pdfLoadOptions().useSystemFonts,
          useWorkerFetch,
          cMapPacked: pdfLoadOptions().cMapPacked,
          isEvalSupported: pdfLoadOptions().isEvalSupported,
          enableXfa: pdfLoadOptions().enableXfa,
          verbosity: pdfLoadOptions().verbosity,
        },
        viewport: {
          containerWidth,
          fitScale: fitScale ?? 1,
          userZoom: zoom,
          renderScale: renderScale ?? 1,
          devicePixelRatio: window.devicePixelRatio || 1,
          page1AtScale1,
          windowInnerWidth: window.innerWidth,
          windowInnerHeight: window.innerHeight,
          visualViewportWidth: vv?.width ?? null,
          visualViewportHeight: vv?.height ?? null,
        },
      };
    },
  }));

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const measure = () => {
      const w = Math.floor(wrap.clientWidth);
      if (w > 0) setContainerWidth(w);
    };
    measure();

    const ro = new ResizeObserver(() => measure());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [pdf]);

  useEffect(() => {
    let cancelled = false;
    let loaded: pdfjs.PDFDocumentProxy | null = null;

    const openOnce = async (): Promise<pdfjs.PDFDocumentProxy> => {
      ensurePdfWorker();
      await preloadPdfRuntime();
      await probePdfAssets();
      const cMapUrl = pdfCMapUrl();
      const standardFontDataUrl = pdfStandardFontDataUrl();
      const loadOpts = pdfLoadOptions();
      const data = new Uint8Array(cloneArrayBuffer(buffer));
      const useWorkerFetch = !Capacitor.isNativePlatform();
      appendLog("info", "pdf", "getDocument options", {
        ...loadOpts,
        cMapUrl,
        standardFontDataUrl,
        useWorkerFetch,
        textLayerDefault: defaultPdfTextLayerEnabled(),
        byteLength: data.byteLength,
      });
      const task = pdfjs.getDocument({
        data,
        ...loadOpts,
        cMapUrl,
        standardFontDataUrl,
        // Main-thread fetch is more reliable for cmaps under Capacitor https://localhost
        useWorkerFetch,
      });
      return withTimeout(task.promise, 45_000, "pdf.getDocument");
    };

    void (async () => {
      try {
        pdfErrors.length = 0;
        appendLog("info", "pdf", "loading document", {
          cMapUrl: pdfCMapUrl(),
          worker: pdfWorkerSrc(),
          disableFontFace: pdfLoadOptions().disableFontFace,
        });
        let doc: pdfjs.PDFDocumentProxy;
        try {
          doc = await openOnce();
        } catch (firstErr) {
          const firstMsg =
            firstErr instanceof Error ? firstErr.message : String(firstErr);
          appendLog("warn", "pdf", "first open failed — retrying once", firstMsg);
          // Cold start after install/OTA: worker/assets may still be settling.
          await new Promise((r) => window.setTimeout(r, 350));
          workerConfigured = false;
          assetsProbed = false;
          ensurePdfWorker();
          await preloadPdfRuntime();
          doc = await openOnce();
        }
        if (cancelled) {
          await doc.destroy();
          return;
        }
        loaded = doc;
        layoutReadyRef.current = false;
        setFitScale(null);
        setRenderScale(null);
        setPdf(doc);
        setPageCount(doc.numPages);
        setReadyGen((g) => g + 1);
        onPageCountRef.current(doc.numPages);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "خطا در بارگذاری PDF";
        pdfErrors.push(message);
        appendLog("error", "pdf", message);
        onErrorRef.current?.(
          message.includes("Missing") || message.includes("cmap")
            ? `فونت/نقشهٔ نویسهٔ PDF بارگذاری نشد: ${message}`
            : message
        );
      }
    })();

    return () => {
      cancelled = true;
      setPdf(null);
      setPageCount(0);
      void loaded?.destroy();
    };
  }, [buffer]);

  useEffect(() => {
    if (!pdf || containerWidth <= 0) return;

    void (async () => {
      try {
        const pg = await pdf.getPage(1);
        const viewport = pg.getViewport({ scale: 1 });
        const width = containerWidth - 4;
        if (width > 0) {
          const next = Math.max(0.35, Math.min(3.5, width / viewport.width));
          setFitScale(next);
          // First layout: apply immediately at 100% fit — no animated zoom-out.
          if (!layoutReadyRef.current) {
            layoutReadyRef.current = true;
            setRenderScale(Math.max(0.25, Math.min(4, next * zoom)));
          }
        }
      } catch {
        /* ignore */
      }
    })();
  }, [pdf, containerWidth, zoom]);

  useEffect(() => {
    if (targetScale == null || !layoutReadyRef.current) return;
    const pinching = document.documentElement.classList.contains("nr-pinching");
    const delay = pinching ? RENDER_DEBOUNCE_PINCH_MS : RENDER_DEBOUNCE_MS;
    const t = window.setTimeout(() => {
      setRenderScale(targetScale);
    }, delay);
    return () => window.clearTimeout(t);
  }, [targetScale]);

  useEffect(() => {
    const root = pagesRef.current;
    if (!root || pageCount === 0) return;

    const ratios = new Map<number, number>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const n = Number((entry.target as HTMLElement).dataset.page);
          if (!Number.isFinite(n)) continue;
          ratios.set(n, entry.intersectionRatio);
        }
        let best = 1;
        let bestRatio = 0;
        for (const [n, r] of ratios) {
          if (r > bestRatio) {
            bestRatio = r;
            best = n;
          }
        }
        if (bestRatio > 0) onVisiblePageRef.current?.(best);
      },
      {
        root: wrapRef.current?.closest(".canvas-scroll") ?? null,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    root.querySelectorAll("[data-page]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [pageCount, readyGen, renderScale]);

  // Keep text-layer caret / selection in view when zoomed wider than the screen
  useEffect(() => {
    if (!textLayerEnabled) return;
    const scrollRoot =
      (wrapRef.current?.closest(".canvas-scroll") as HTMLElement | null) ?? null;
    if (!scrollRoot) return;

    let raf = 0;
    const ensureVisible = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const anchor = sel.anchorNode;
      if (!anchor || !wrapRef.current?.contains(anchor)) return;
      const range = sel.getRangeAt(0);
      let rect = range.getBoundingClientRect();
      if ((rect.width === 0 && rect.height === 0) || !Number.isFinite(rect.top)) {
        const node =
          range.startContainer instanceof Element
            ? range.startContainer
            : range.startContainer.parentElement;
        if (!node) return;
        rect = node.getBoundingClientRect();
      }
      const rootRect = scrollRoot.getBoundingClientRect();
      const pad = 28;
      let dx = 0;
      let dy = 0;
      if (rect.left < rootRect.left + pad) dx = rect.left - rootRect.left - pad;
      else if (rect.right > rootRect.right - pad)
        dx = rect.right - rootRect.right + pad;
      if (rect.top < rootRect.top + pad) dy = rect.top - rootRect.top - pad;
      else if (rect.bottom > rootRect.bottom - pad)
        dy = rect.bottom - rootRect.bottom + pad;
      if (dx || dy) scrollRoot.scrollBy({ left: dx, top: dy, behavior: "auto" });
    };

    const onSel = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(ensureVisible);
    };
    document.addEventListener("selectionchange", onSel);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("selectionchange", onSel);
    };
  }, [textLayerEnabled, targetScale, readyGen]);

  const pinsByPage = commentPins.reduce<Record<number, PdfCommentPin[]>>(
    (acc, pin) => {
      (acc[pin.page] ??= []).push(pin);
      return acc;
    },
    {}
  );

  return (
    <div
      ref={wrapRef}
      className="pdf-wrap"
      // LTR: PDF page coords / canvas are LTR; RTL here skews layout & zoom origin.
      dir="ltr"
      lang="fa"
    >
      <div
        ref={pagesRef}
        className={`pdf-pages${textLayerEnabled ? "" : " canvas-only"}`}
      >
        {pdf &&
          renderScale != null &&
          Array.from({ length: pageCount }, (_, i) => (
            <PdfPage
              key={`${readyGen}-${i + 1}-${textLayerEnabled ? "t" : "c"}`}
              pdf={pdf}
              pageNumber={i + 1}
              scale={renderScale}
              textLayerEnabled={textLayerEnabled}
              pins={pinsByPage[i + 1] ?? []}
              onError={onError}
              onCommentPinClick={onCommentPinClick}
              onLongPressComment={onLongPressComment}
            />
          ))}
        {pdf && renderScale == null && (
          <div className="pdf-layout-pending" aria-busy="true">
            در حال آماده‌سازی…
          </div>
        )}
      </div>
    </div>
  );
});

type PageProps = {
  pdf: pdfjs.PDFDocumentProxy;
  pageNumber: number;
  scale: number;
  textLayerEnabled: boolean;
  pins: PdfCommentPin[];
  onError?: (message: string) => void;
  onCommentPinClick?: (id: string) => void;
  onLongPressComment?: (info: {
    page: number;
    quote: string;
    xNorm: number;
    yNorm: number;
  }) => void;
};

function PdfPage({
  pdf,
  pageNumber,
  scale,
  textLayerEnabled,
  pins,
  onError,
  onCommentPinClick,
  onLongPressComment,
}: PageProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textLayerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(pageNumber <= 2);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const longPressRef = useRef<{
    timer: number;
    x: number;
    y: number;
  } | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const scrollRoot = host.closest(".canvas-scroll");
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setVisible(true);
        }
      },
      {
        root: scrollRoot,
        rootMargin: "200% 0px",
        threshold: 0.01,
      }
    );
    io.observe(host);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let cancelled = false;
    void (async () => {
      try {
        const pg = await pdf.getPage(pageNumber);
        if (cancelled) return;
        const viewport = pg.getViewport({ scale });
        host.style.minHeight = `${Math.ceil(viewport.height)}px`;
        host.style.width = `${Math.ceil(viewport.width)}px`;
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdf, pageNumber, scale]);

  useEffect(() => {
    if (!visible) return;
    const canvas = canvasRef.current;
    const textLayerDiv = textLayerRef.current;
    if (!canvas) return;

    let cancelled = false;
    let renderTask: pdfjs.RenderTask | null = null;

    void (async () => {
      try {
        const pg = await pdf.getPage(pageNumber);
        if (cancelled) return;
        const viewport = pg.getViewport({ scale });
        const ctx = canvas.getContext("2d", {
          alpha: false,
          willReadFrequently: false,
        });
        if (!ctx) return;

        const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
        const cssW = viewport.width;
        const cssH = viewport.height;
        canvas.width = Math.floor(cssW * dpr);
        canvas.height = Math.floor(cssH * dpr);
        canvas.style.width = `${cssW}px`;
        canvas.style.height = `${cssH}px`;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, cssW, cssH);

        renderTask = pg.render({
          canvasContext: ctx,
          viewport,
          intent: "display",
        });
        await renderTask.promise;
        if (cancelled) return;

        // Canvas already has correctly shaped glyphs; text layer is optional
        // (selection) and often jumbles RTL on WebView — keep it inert visually.
        if (textLayerEnabled && textLayerDiv) {
          textLayerDiv.innerHTML = "";
          textLayerDiv.style.width = `${cssW}px`;
          textLayerDiv.style.height = `${cssH}px`;
          textLayerDiv.setAttribute("dir", "rtl");
          textLayerDiv.setAttribute("lang", "fa");
          try {
            const textContent = await pg.getTextContent({
              includeMarkedContent: false,
            });
            if (cancelled) return;
            const TextLayerCtor = (
              pdfjs as unknown as {
                TextLayer?: new (opts: {
                  textContentSource: unknown;
                  container: HTMLElement;
                  viewport: unknown;
                }) => { render: () => Promise<void> };
              }
            ).TextLayer;
            if (TextLayerCtor) {
              const textLayer = new TextLayerCtor({
                textContentSource: textContent,
                container: textLayerDiv,
                viewport,
              });
              await textLayer.render();
            }
          } catch (err) {
            const msg =
              err instanceof Error ? err.message : "text layer failed";
            pdfErrors.push(msg);
            appendLog("warn", "pdf", msg);
          }
        } else if (textLayerDiv) {
          textLayerDiv.innerHTML = "";
        }
      } catch (err) {
        if (cancelled) return;
        if (
          err &&
          typeof err === "object" &&
          "name" in err &&
          (err as { name: string }).name === "RenderingCancelledException"
        ) {
          return;
        }
        const message =
          err instanceof Error ? err.message : "خطا در رندر PDF";
        pdfErrors.push(`page ${pageNumber}: ${message}`);
        appendLog("error", "pdf", `render page ${pageNumber}`, message);
        onErrorRef.current?.(message);
      }
    })();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, pageNumber, scale, visible, textLayerEnabled]);

  return (
    <div
      ref={hostRef}
      className="pdf-page-host"
      data-page={pageNumber}
      aria-label={`صفحه ${pageNumber}`}
      dir="ltr"
      onPointerDown={(e) => {
        if (!onLongPressComment || e.pointerType === "mouse" && e.button !== 0)
          return;
        if (e.pointerType === "mouse") return; // long-press is touch-first
        const host = hostRef.current;
        if (!host) return;
        const rect = host.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;
        longPressRef.current = {
          x,
          y,
          timer: window.setTimeout(() => {
            const xNorm = Math.min(1, Math.max(0, (x - rect.left) / rect.width));
            const yNorm = Math.min(1, Math.max(0, (y - rect.top) / rect.height));
            const sel = window.getSelection()?.toString().trim() ?? "";
            let quote = sel;
            if (!quote && textLayerRef.current) {
              // nearest text span under finger
              const el = document.elementFromPoint(x, y) as HTMLElement | null;
              const span = el?.closest("span") as HTMLElement | null;
              quote = (span?.textContent || "").trim().slice(0, 160);
            }
            if (!quote) {
              quote = `یادداشت روی صفحه ${pageNumber.toLocaleString("fa-IR")}`;
            }
            onLongPressComment({
              page: pageNumber,
              quote,
              xNorm,
              yNorm,
            });
            longPressRef.current = null;
          }, 1000),
        };
      }}
      onPointerMove={(e) => {
        const lp = longPressRef.current;
        if (!lp) return;
        if (Math.hypot(e.clientX - lp.x, e.clientY - lp.y) > 14) {
          window.clearTimeout(lp.timer);
          longPressRef.current = null;
        }
      }}
      onPointerUp={() => {
        if (longPressRef.current) {
          window.clearTimeout(longPressRef.current.timer);
          longPressRef.current = null;
        }
      }}
      onPointerCancel={() => {
        if (longPressRef.current) {
          window.clearTimeout(longPressRef.current.timer);
          longPressRef.current = null;
        }
      }}
    >
      {visible ? (
        <div className="pdf-page-stack">
          <canvas ref={canvasRef} className="pdf-page" />
          {textLayerEnabled && (
            <div
              ref={textLayerRef}
              className="pdf-text-layer"
              dir="rtl"
              lang="fa"
            />
          )}
          {pins.map((pin) => (
            <button
              key={pin.id}
              type="button"
              className="pdf-comment-pin"
              style={{
                left: `${pin.xNorm * 100}%`,
                top: `${pin.yNorm * 100}%`,
              }}
              aria-label="باز کردن یادداشت"
              title="یادداشت"
              onClick={(e) => {
                e.stopPropagation();
                onCommentPinClick?.(pin.id);
              }}
              onPointerDown={(e) => e.stopPropagation()}
            />
          ))}
        </div>
      ) : (
        <div className="pdf-page-placeholder" aria-hidden />
      )}
    </div>
  );
}
